"use client";

import { c as _c } from "react/compiler-runtime";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import ContentDisplay from "@/components/waves/drops/ContentDisplay";
import { buildProcessedContent } from "@/components/waves/drops/media-utils";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { useInView } from "@/hooks/useInView";
import { memo, useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import BoostedDropLinkPreview from "./BoostedDropLinkPreview";
import { extractFirstUrl, removePreviewUrlFromContent } from "./extractStandaloneUrl";
type BoostedDropPart = ApiDrop["parts"][number];
type BoostedDropMediaItem = BoostedDropPart["media"][number];
type MeasuredChatMediaAspectRatio = {
  readonly aspectRatio: string;
  readonly mimeType: string;
  readonly url: string;
};
type OverflowElements = {
  readonly container: HTMLDivElement;
  readonly preview: HTMLDivElement;
  readonly textMeasure: HTMLDivElement;
  readonly wrapper: HTMLDivElement;
};
const CHAT_MEDIA_FALLBACK_ASPECT_RATIO = "8 / 5";
const HOME_ASPECT_RATIO_CLASSES = "tw-aspect-[2/1] sm:tw-aspect-[5/4] md:tw-aspect-[8/5] lg:tw-aspect-[5/4] xl:tw-aspect-[8/5]";
const CHAT_BODY_INSET_X_CLASSES = "tw-px-3 sm:tw-px-4";
const CHAT_PREVIEW_CONTAINER_CLASSES = "tw-relative tw-flex tw-w-full tw-flex-col tw-items-stretch tw-justify-start tw-gap-3 tw-overflow-hidden tw-rounded-xl";
const CHAT_TEXT_CONTAINER_CLASSES = "tw-relative tw-flex tw-w-full tw-items-start tw-justify-start tw-overflow-hidden tw-rounded-xl tw-px-4 tw-pb-4 tw-pt-14";
const CHAT_SUPPLEMENTAL_TEXT_CONTAINER_CLASSES = "tw-relative tw-flex tw-w-full tw-items-start tw-justify-start tw-overflow-hidden tw-rounded-xl tw-px-4 tw-pb-4";
const HOME_PREVIEW_CONTAINER_CLASSES = `tw-relative tw-flex ${HOME_ASPECT_RATIO_CLASSES} tw-w-full tw-flex-col tw-items-stretch tw-justify-stretch tw-gap-3 tw-overflow-hidden tw-rounded-xl`;
const HOME_TEXT_CONTAINER_CLASSES = `tw-relative tw-flex ${HOME_ASPECT_RATIO_CLASSES} tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl tw-px-6 tw-pb-6 sm:tw-pb-0 tw-pt-4 sm:tw-pt-16 md:tw-pt-12`;
const CHAT_MEDIA_WITH_CONTENT_CLASSES = "tw-flex tw-w-full tw-flex-col tw-gap-3";
const CHAT_PREVIEW_WRAPPER_BASE_CLASSES = "tw-relative tw-z-10 tw-flex tw-w-full tw-min-w-0 tw-max-w-full tw-flex-col";
const HOME_PREVIEW_WRAPPER_BASE_CLASSES = "tw-relative tw-z-10 tw-grid tw-h-full tw-w-full tw-min-w-0 tw-max-w-full tw-grid-rows-[minmax(0,1fr)_auto]";
const CHAT_TEXT_WRAPPER_CLASSES = "tw-relative tw-z-10 tw-flex tw-w-full tw-min-w-0 tw-max-w-full tw-items-start tw-justify-start";
const HOME_TEXT_WRAPPER_CLASSES = "tw-relative tw-z-10 tw-flex tw-h-full tw-w-full tw-min-w-0 tw-max-w-full tw-items-center tw-justify-center";
const WITH_PREVIEW_CONTENT_CLASS_NAME = "tw-flex tw-w-full tw-max-w-full tw-flex-col tw-items-start tw-gap-1 tw-break-words tw-px-3 tw-pb-3 sm:tw-px-4 sm:tw-pb-4 tw-tracking-[0.01em] tw-font-normal tw-text-iron-300";
const CHAT_TEXT_CONTENT_CLASS_NAME = "tw-flex tw-w-full tw-max-w-full tw-flex-col tw-items-start tw-gap-1 tw-break-words tw-tracking-[0.01em] tw-font-normal tw-text-iron-300";
const HOME_TEXT_CONTENT_CLASS_NAME = "tw-flex tw-w-fit tw-max-w-full tw-flex-col tw-items-start tw-gap-1 tw-break-words tw-tracking-[0.01em] tw-font-normal tw-text-iron-300";
const WITH_PREVIEW_TEXT_CLAMP_CLASS = "tw-line-clamp-2 sm:tw-line-clamp-3";
const WITHOUT_PREVIEW_TEXT_CLAMP_CLASS = "tw-line-clamp-6";
const CONTENT_TEXT_BASE_CLASSES = "tw-max-w-full tw-break-words tw-tracking-[0.01em] tw-font-normal";
const MAX_OVERFLOW_MEASUREMENT_ATTEMPTS = 12;
const OVERFLOW_MEASUREMENT_INTERVAL_MS = 200;
interface BoostedDropCardHomeContentProps {
  readonly part?: BoostedDropPart | undefined;
  readonly isChatVariant: boolean;
}
type BoostedDropOverflowMeasurementOptions = {
  readonly hasPreview: boolean;
  readonly hasTextContent: boolean;
  readonly isChatVariant: boolean;
};
const getContentContainerClasses = ({
  hasPreview,
  isSupplementalChatContent,
  isChatVariant
}: {
  readonly hasPreview: boolean;
  readonly isSupplementalChatContent: boolean;
  readonly isChatVariant: boolean;
}): string => {
  if (isChatVariant) {
    if (!hasPreview && isSupplementalChatContent) {
      return CHAT_SUPPLEMENTAL_TEXT_CONTAINER_CLASSES;
    }
    return hasPreview ? CHAT_PREVIEW_CONTAINER_CLASSES : CHAT_TEXT_CONTAINER_CLASSES;
  }
  return hasPreview ? HOME_PREVIEW_CONTAINER_CLASSES : HOME_TEXT_CONTAINER_CLASSES;
};
const getSupplementalChatPart = (part?: BoostedDropPart): BoostedDropPart | null => {
  if (!part) {
    return null;
  }
  const remainingMedia = part.media.slice(1);
  const hasCaption = Boolean(part.content?.trim());
  if (!hasCaption && remainingMedia.length === 0) {
    return null;
  }
  return {
    ...part,
    media: remainingMedia
  };
};
const getContentWrapperClasses = ({
  hasPreview,
  hasTextContent,
  isChatVariant
}: {
  readonly hasPreview: boolean;
  readonly hasTextContent: boolean;
  readonly isChatVariant: boolean;
}): string => {
  if (!hasPreview) {
    return isChatVariant ? CHAT_TEXT_WRAPPER_CLASSES : HOME_TEXT_WRAPPER_CLASSES;
  }
  const baseClasses = isChatVariant ? CHAT_PREVIEW_WRAPPER_BASE_CLASSES : HOME_PREVIEW_WRAPPER_BASE_CLASSES;
  const gapClasses = hasTextContent ? "tw-gap-2 sm:tw-gap-3" : "";
  return gapClasses ? `${baseClasses} ${gapClasses}` : baseClasses;
};
const getContentClassName = ({
  hasPreview,
  isChatVariant
}: {
  readonly hasPreview: boolean;
  readonly isChatVariant: boolean;
}): string => {
  if (hasPreview) {
    return WITH_PREVIEW_CONTENT_CLASS_NAME;
  }
  return isChatVariant ? CHAT_TEXT_CONTENT_CLASS_NAME : HOME_TEXT_CONTENT_CLASS_NAME;
};
const getContentTextClassName = (hasPreview: boolean): string => {
  const clampClasses = hasPreview ? WITH_PREVIEW_TEXT_CLAMP_CLASS : WITHOUT_PREVIEW_TEXT_CLAMP_CLASS;
  return `${clampClasses} ${CONTENT_TEXT_BASE_CLASSES}`;
};
const getChatBodyInsetClasses = ({
  addBottomPadding
}: {
  readonly addBottomPadding: boolean;
}): string => `${CHAT_BODY_INSET_X_CLASSES}${addBottomPadding ? " tw-pb-4" : ""}`;
const getOverflowElements = ({
  container,
  preview,
  textMeasure,
  wrapper
}: {
  readonly container: HTMLDivElement | null;
  readonly preview: HTMLDivElement | null;
  readonly textMeasure: HTMLDivElement | null;
  readonly wrapper: HTMLDivElement | null;
}): OverflowElements | null => {
  if (!container || !preview || !textMeasure || !wrapper) {
    return null;
  }
  return {
    container,
    preview,
    textMeasure,
    wrapper
  };
};
const shouldHideOverflowText = ({
  container,
  preview,
  textMeasure,
  wrapper
}: OverflowElements): boolean => {
  const containerHeight = container.getBoundingClientRect().height;
  const previewHeight = preview.getBoundingClientRect().height;
  const previewScrollHeight = preview.scrollHeight;
  const previewClientHeight = preview.clientHeight;
  const textHeight = textMeasure.getBoundingClientRect().height;
  const computedStyles = getComputedStyle(wrapper);
  const rowGap = Number.parseFloat(computedStyles.rowGap || "0");
  const previewOverflowing = previewScrollHeight > previewClientHeight + 1;
  const totalHeight = Math.max(previewHeight, previewScrollHeight) + textHeight + rowGap;
  return previewOverflowing || totalHeight > containerHeight + 1;
};
const observeIfPresent = (observer: ResizeObserver, element: HTMLDivElement | null) => {
  if (element) {
    observer.observe(element);
  }
};
const cancelAnimationFrameIfNeeded = (frame: number | undefined) => {
  if (typeof frame === "number") {
    globalThis.cancelAnimationFrame(frame);
  }
};
const useBoostedDropPreviewContent = part => {
  const $ = _c(17);
  part?.content;
  const t0 = part?.content;
  let t1;
  if ($[0] !== t0) {
    t1 = extractFirstUrl(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previewUrl = t1;
  let t2;
  if ($[2] !== part?.content || $[3] !== previewUrl) {
    t2 = () => previewUrl ? removePreviewUrlFromContent(part?.content, previewUrl) : part?.content;
    $[2] = part?.content;
    $[3] = previewUrl;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  part?.content;
  let t3;
  if ($[5] !== t2) {
    t3 = t2();
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const cleanedContent = t3;
  part?.media;
  const t4 = cleanedContent ?? null;
  const t5 = part?.media;
  const t6 = previewUrl ? "" : "View drop...";
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = buildProcessedContent(t4, t5, t6);
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const previewContent = t7;
  let t8;
  if ($[11] !== cleanedContent) {
    t8 = cleanedContent && cleanedContent.trim().length > 0;
    $[11] = cleanedContent;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const hasTextContent = Boolean(t8);
  let t9;
  if ($[13] !== hasTextContent || $[14] !== previewContent || $[15] !== previewUrl) {
    t9 = {
      hasTextContent,
      previewContent,
      previewUrl
    };
    $[13] = hasTextContent;
    $[14] = previewContent;
    $[15] = previewUrl;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
};
const useOverflowResizeTracking = t0 => {
  const $ = _c(7);
  const {
    enabled,
    measureOverflow,
    contentContainerRef,
    previewRef,
    textMeasureRef
  } = t0;
  let t1;
  let t2;
  if ($[0] !== contentContainerRef || $[1] !== enabled || $[2] !== measureOverflow || $[3] !== previewRef || $[4] !== textMeasureRef) {
    t1 = () => {
      if (!enabled) {
        return;
      }
      const frame = globalThis.requestAnimationFrame(() => {
        measureOverflow();
      });
      if (typeof ResizeObserver === "undefined") {
        const handleResize = () => measureOverflow();
        globalThis.addEventListener("resize", handleResize);
        return () => {
          globalThis.removeEventListener("resize", handleResize);
          cancelAnimationFrameIfNeeded(frame);
        };
      }
      const observer = new ResizeObserver(() => measureOverflow());
      observeIfPresent(observer, contentContainerRef.current);
      observeIfPresent(observer, previewRef.current);
      observeIfPresent(observer, textMeasureRef.current);
      return () => {
        observer.disconnect();
        cancelAnimationFrameIfNeeded(frame);
      };
    };
    t2 = [contentContainerRef, enabled, measureOverflow, previewRef, textMeasureRef];
    $[0] = contentContainerRef;
    $[1] = enabled;
    $[2] = measureOverflow;
    $[3] = previewRef;
    $[4] = textMeasureRef;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
};
const useOverflowMeasurementRetry = t0 => {
  const $ = _c(4);
  const {
    enabled,
    measureOverflow
  } = t0;
  let t1;
  let t2;
  if ($[0] !== enabled || $[1] !== measureOverflow) {
    t1 = () => {
      if (!enabled) {
        return;
      }
      let attempts = 0;
      const intervalId = globalThis.setInterval(() => {
        measureOverflow();
        attempts = attempts + 1;
        attempts;
        if (attempts >= MAX_OVERFLOW_MEASUREMENT_ATTEMPTS) {
          globalThis.clearInterval(intervalId);
        }
      }, OVERFLOW_MEASUREMENT_INTERVAL_MS);
      return () => {
        if (typeof intervalId === "number") {
          globalThis.clearInterval(intervalId);
        }
      };
    };
    t2 = [enabled, measureOverflow];
    $[0] = enabled;
    $[1] = measureOverflow;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
};
const useBoostedDropOverflowMeasurement = t0 => {
  const $ = _c(11);
  const {
    hasPreview,
    hasTextContent,
    isChatVariant
  } = t0;
  const contentContainerRef = useRef(null);
  const contentWrapperRef = useRef(null);
  const previewRef = useRef(null);
  const textMeasureRef = useRef(null);
  const [hideOverflowText, setHideOverflowText] = useState(false);
  const [hasMeasuredOverflow, setHasMeasuredOverflow] = useState(false);
  const shouldMeasureOverflow = !isChatVariant && hasPreview && hasTextContent;
  let t1;
  if ($[0] !== shouldMeasureOverflow) {
    t1 = () => {
      if (!shouldMeasureOverflow) {
        return;
      }
      const elements = getOverflowElements({
        container: contentContainerRef.current,
        preview: previewRef.current,
        textMeasure: textMeasureRef.current,
        wrapper: contentWrapperRef.current
      });
      if (!elements) {
        setHasMeasuredOverflow(false);
        setHideOverflowText(false);
        return;
      }
      setHasMeasuredOverflow(true);
      setHideOverflowText(shouldHideOverflowText(elements));
    };
    $[0] = shouldMeasureOverflow;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const measureOverflow = t1;
  let t2;
  if ($[2] !== measureOverflow || $[3] !== shouldMeasureOverflow) {
    t2 = {
      contentContainerRef,
      enabled: shouldMeasureOverflow,
      measureOverflow,
      previewRef,
      textMeasureRef
    };
    $[2] = measureOverflow;
    $[3] = shouldMeasureOverflow;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useOverflowResizeTracking(t2);
  let t3;
  if ($[5] !== measureOverflow || $[6] !== shouldMeasureOverflow) {
    t3 = {
      enabled: shouldMeasureOverflow,
      measureOverflow
    };
    $[5] = measureOverflow;
    $[6] = shouldMeasureOverflow;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useOverflowMeasurementRetry(t3);
  const shouldShowTextContent = !hasPreview || hasTextContent && (!shouldMeasureOverflow || !hasMeasuredOverflow || !hideOverflowText);
  let t4;
  if ($[8] !== shouldMeasureOverflow || $[9] !== shouldShowTextContent) {
    t4 = {
      contentContainerRef,
      contentWrapperRef,
      previewRef,
      shouldMeasureOverflow,
      shouldShowTextContent,
      textMeasureRef
    };
    $[8] = shouldMeasureOverflow;
    $[9] = shouldShowTextContent;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const BoostedDropCardChatMedia = memo(t0 => {
  const $ = _c(30);
  const {
    media
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      threshold: 0.1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [frameRef, inView] = useInView(t1);
  const [measuredAspectRatio, setMeasuredAspectRatio] = useState(null);
  const aspectRatio = measuredAspectRatio?.url === media.url && measuredAspectRatio.mimeType === media.mime_type ? measuredAspectRatio.aspectRatio : CHAT_MEDIA_FALLBACK_ASPECT_RATIO;
  let t2;
  if ($[1] !== media.mime_type || $[2] !== media.url) {
    t2 = nextAspectRatio => {
      setMeasuredAspectRatio(current => {
        if (current?.url === media.url && current.mimeType === media.mime_type && current.aspectRatio === nextAspectRatio) {
          return current;
        }
        return {
          url: media.url,
          mimeType: media.mime_type,
          aspectRatio: nextAspectRatio
        };
      });
    };
    $[1] = media.mime_type;
    $[2] = media.url;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const syncMeasuredAspectRatio = t2;
  let t3;
  if ($[4] !== media.mime_type) {
    t3 = media.mime_type.includes("image");
    $[4] = media.mime_type;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isImageMedia = t3;
  let t4;
  if ($[6] !== media.mime_type) {
    t4 = media.mime_type.includes("video");
    $[6] = media.mime_type;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const isVideoMedia = t4;
  let t5;
  if ($[8] !== inView || $[9] !== isImageMedia || $[10] !== isVideoMedia || $[11] !== media.url || $[12] !== syncMeasuredAspectRatio) {
    t5 = () => {
      if (typeof window === "undefined" || !inView) {
        return;
      }
      let isCancelled = false;
      if (isImageMedia) {
        const image = new globalThis.Image();
        image.onload = () => {
          if (isCancelled || image.naturalWidth <= 0 || image.naturalHeight <= 0) {
            return;
          }
          syncMeasuredAspectRatio(`${image.naturalWidth} / ${image.naturalHeight}`);
        };
        image.src = getScaledImageUri(media.url, ImageScale.AUTOx450);
        return () => {
          isCancelled = true;
        };
      }
      if (isVideoMedia) {
        const video = document.createElement("video");
        const handleLoadedMetadata = () => {
          if (isCancelled || video.videoWidth <= 0 || video.videoHeight <= 0) {
            return;
          }
          syncMeasuredAspectRatio(`${video.videoWidth} / ${video.videoHeight}`);
        };
        video.preload = "metadata";
        video.addEventListener("loadedmetadata", handleLoadedMetadata);
        video.src = media.url;
        video.load();
        return () => {
          isCancelled = true;
          video.removeEventListener("loadedmetadata", handleLoadedMetadata);
          video.removeAttribute("src");
          video.load();
        };
      }
      return () => {
        isCancelled = true;
      };
    };
    $[8] = inView;
    $[9] = isImageMedia;
    $[10] = isVideoMedia;
    $[11] = media.url;
    $[12] = syncMeasuredAspectRatio;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== inView || $[15] !== isImageMedia || $[16] !== isVideoMedia || $[17] !== media.mime_type || $[18] !== media.url || $[19] !== syncMeasuredAspectRatio) {
    t6 = [inView, isImageMedia, isVideoMedia, media.mime_type, media.url, syncMeasuredAspectRatio];
    $[14] = inView;
    $[15] = isImageMedia;
    $[16] = isVideoMedia;
    $[17] = media.mime_type;
    $[18] = media.url;
    $[19] = syncMeasuredAspectRatio;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  useEffect(t5, t6);
  let t7;
  if ($[21] !== aspectRatio) {
    t7 = {
      aspectRatio
    };
    $[21] = aspectRatio;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== media.mime_type || $[24] !== media.url) {
    t8 = <DropListItemContentMedia media_mime_type={media.mime_type} media_url={media.url} imageScale={ImageScale.AUTOx450} imageObjectPosition="center" />;
    $[23] = media.mime_type;
    $[24] = media.url;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== frameRef || $[27] !== t7 || $[28] !== t8) {
    t9 = <div data-testid="boosted-drop-media-frame" className="tw-relative tw-w-full tw-overflow-hidden tw-rounded-xl tw-bg-black/20" ref={frameRef} style={t7}>{t8}</div>;
    $[26] = frameRef;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
});
BoostedDropCardChatMedia.displayName = "BoostedDropCardChatMedia";
const BoostedDropCardHomeMedia = memo(t0 => {
  const $ = _c(3);
  const {
    media
  } = t0;
  let t1;
  if ($[0] !== media.mime_type || $[1] !== media.url) {
    t1 = <div data-testid="boosted-drop-media-frame" className={`tw-relative ${HOME_ASPECT_RATIO_CLASSES} tw-w-full tw-overflow-hidden tw-rounded-xl`}><div className="tw-relative tw-h-full tw-w-full"><div className="tw-relative tw-z-0 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-xl tw-transition-transform tw-duration-700 group-hover:tw-scale-[1.02]"><div className="tw-relative tw-h-full tw-w-full tw-overflow-hidden"><DropListItemContentMedia media_mime_type={media.mime_type} media_url={media.url} imageScale={ImageScale.AUTOx450} imageObjectPosition="center" /></div></div></div></div>;
    $[0] = media.mime_type;
    $[1] = media.url;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
});
BoostedDropCardHomeMedia.displayName = "BoostedDropCardHomeMedia";
const BoostedDropCardTextSection = memo(t0 => {
  const $ = _c(52);
  const {
    isChatVariant,
    isSupplementalChatContent: t1,
    part
  } = t0;
  const isSupplementalChatContent = t1 === undefined ? false : t1;
  const {
    hasTextContent,
    previewContent,
    previewUrl
  } = useBoostedDropPreviewContent(part);
  const hasPreview = Boolean(previewUrl);
  let t2;
  if ($[0] !== hasPreview || $[1] !== hasTextContent || $[2] !== isChatVariant) {
    t2 = {
      hasPreview,
      hasTextContent,
      isChatVariant
    };
    $[0] = hasPreview;
    $[1] = hasTextContent;
    $[2] = isChatVariant;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    contentContainerRef,
    contentWrapperRef,
    previewRef,
    shouldMeasureOverflow,
    shouldShowTextContent,
    textMeasureRef
  } = useBoostedDropOverflowMeasurement(t2);
  let t3;
  if ($[4] !== hasPreview || $[5] !== isChatVariant || $[6] !== isSupplementalChatContent) {
    t3 = getContentContainerClasses({
      hasPreview,
      isSupplementalChatContent,
      isChatVariant
    });
    $[4] = hasPreview;
    $[5] = isChatVariant;
    $[6] = isSupplementalChatContent;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const contentContainerClasses = t3;
  let t4;
  if ($[8] !== hasPreview || $[9] !== hasTextContent || $[10] !== isChatVariant) {
    t4 = getContentWrapperClasses({
      hasPreview,
      hasTextContent,
      isChatVariant
    });
    $[8] = hasPreview;
    $[9] = hasTextContent;
    $[10] = isChatVariant;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const contentWrapperClasses = t4;
  let t5;
  if ($[12] !== hasPreview || $[13] !== isChatVariant) {
    t5 = getContentClassName({
      hasPreview,
      isChatVariant
    });
    $[12] = hasPreview;
    $[13] = isChatVariant;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const contentClassName = t5;
  let t6;
  if ($[15] !== hasPreview) {
    t6 = getContentTextClassName(hasPreview);
    $[15] = hasPreview;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const contentTextClassName = t6;
  let t7;
  if ($[17] !== hasTextContent || $[18] !== isChatVariant) {
    t7 = isChatVariant ? `${getChatBodyInsetClasses({
      addBottomPadding: !hasTextContent
    })} tw-min-h-0 tw-overflow-hidden` : "tw-min-h-0 tw-overflow-hidden";
    $[17] = hasTextContent;
    $[18] = isChatVariant;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const previewWrapperClassName = t7;
  let t8;
  if ($[20] !== isChatVariant || $[21] !== previewRef || $[22] !== previewUrl || $[23] !== previewWrapperClassName) {
    t8 = previewUrl && <div className={previewWrapperClassName} ref={previewRef}><BoostedDropLinkPreview href={previewUrl} variant={isChatVariant ? "chat" : "home"} /></div>;
    $[20] = isChatVariant;
    $[21] = previewRef;
    $[22] = previewUrl;
    $[23] = previewWrapperClassName;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== contentClassName || $[26] !== contentTextClassName || $[27] !== hasTextContent || $[28] !== previewContent || $[29] !== previewUrl || $[30] !== shouldMeasureOverflow || $[31] !== textMeasureRef) {
    t9 = shouldMeasureOverflow && previewUrl && hasTextContent && <div ref={textMeasureRef} aria-hidden="true" className="tw-pointer-events-none tw-invisible tw-absolute tw-left-0 tw-right-0 tw-top-0"><ContentDisplay content={previewContent} shouldClamp={false} className={contentClassName} textClassName={contentTextClassName} /></div>;
    $[25] = contentClassName;
    $[26] = contentTextClassName;
    $[27] = hasTextContent;
    $[28] = previewContent;
    $[29] = previewUrl;
    $[30] = shouldMeasureOverflow;
    $[31] = textMeasureRef;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== contentClassName || $[34] !== contentTextClassName || $[35] !== previewContent || $[36] !== shouldShowTextContent) {
    t10 = shouldShowTextContent && <ContentDisplay content={previewContent} shouldClamp={false} className={contentClassName} textClassName={contentTextClassName} />;
    $[33] = contentClassName;
    $[34] = contentTextClassName;
    $[35] = previewContent;
    $[36] = shouldShowTextContent;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== contentWrapperClasses || $[39] !== contentWrapperRef || $[40] !== t10 || $[41] !== t8 || $[42] !== t9) {
    t11 = <div className={contentWrapperClasses} ref={contentWrapperRef}>{t8}{t9}{t10}</div>;
    $[38] = contentWrapperClasses;
    $[39] = contentWrapperRef;
    $[40] = t10;
    $[41] = t8;
    $[42] = t9;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== hasPreview || $[45] !== isChatVariant) {
    t12 = !hasPreview && !isChatVariant && <div className="tw-pointer-events-none tw-absolute tw-inset-x-0.5 tw-bottom-0 tw-z-20 tw-h-6 tw-bg-gradient-to-b tw-from-black/0 tw-to-black/70 sm:tw-h-10 md:tw-h-12" />;
    $[44] = hasPreview;
    $[45] = isChatVariant;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== contentContainerClasses || $[48] !== contentContainerRef || $[49] !== t11 || $[50] !== t12) {
    t13 = <div data-testid="boosted-drop-content-frame" className={contentContainerClasses} ref={contentContainerRef}>{t11}{t12}</div>;
    $[47] = contentContainerClasses;
    $[48] = contentContainerRef;
    $[49] = t11;
    $[50] = t12;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  return t13;
});
BoostedDropCardTextSection.displayName = "BoostedDropCardTextSection";
const BoostedDropCardHomeContent = memo(t0 => {
  const $ = _c(26);
  const {
    part,
    isChatVariant
  } = t0;
  const media = part?.media[0];
  let t1;
  if ($[0] !== isChatVariant || $[1] !== media || $[2] !== part) {
    t1 = media && isChatVariant ? getSupplementalChatPart(part) : null;
    $[0] = isChatVariant;
    $[1] = media;
    $[2] = part;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const supplementalChatPart = t1;
  if (!media) {
    let t2;
    if ($[4] !== isChatVariant || $[5] !== part) {
      t2 = <BoostedDropCardTextSection isChatVariant={isChatVariant} part={part} />;
      $[4] = isChatVariant;
      $[5] = part;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  if (isChatVariant) {
    if (supplementalChatPart) {
      let t2;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = getChatBodyInsetClasses({
          addBottomPadding: false
        });
        $[7] = t2;
      } else {
        t2 = $[7];
      }
      let t3;
      if ($[8] !== media) {
        t3 = <BoostedDropCardChatMedia media={media} />;
        $[8] = media;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      let t4;
      if ($[10] !== t2 || $[11] !== t3) {
        t4 = <div className={t2}>{t3}</div>;
        $[10] = t2;
        $[11] = t3;
        $[12] = t4;
      } else {
        t4 = $[12];
      }
      let t5;
      if ($[13] !== supplementalChatPart) {
        t5 = <BoostedDropCardTextSection isChatVariant={true} isSupplementalChatContent={true} part={supplementalChatPart} />;
        $[13] = supplementalChatPart;
        $[14] = t5;
      } else {
        t5 = $[14];
      }
      let t6;
      if ($[15] !== t4 || $[16] !== t5) {
        t6 = <div className={CHAT_MEDIA_WITH_CONTENT_CLASSES}>{t4}{t5}</div>;
        $[15] = t4;
        $[16] = t5;
        $[17] = t6;
      } else {
        t6 = $[17];
      }
      return t6;
    }
    let t2;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = getChatBodyInsetClasses({
        addBottomPadding: true
      });
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    let t3;
    if ($[19] !== media) {
      t3 = <BoostedDropCardChatMedia media={media} />;
      $[19] = media;
      $[20] = t3;
    } else {
      t3 = $[20];
    }
    let t4;
    if ($[21] !== t2 || $[22] !== t3) {
      t4 = <div className={t2}>{t3}</div>;
      $[21] = t2;
      $[22] = t3;
      $[23] = t4;
    } else {
      t4 = $[23];
    }
    return t4;
  }
  let t2;
  if ($[24] !== media) {
    t2 = <BoostedDropCardHomeMedia media={media} />;
    $[24] = media;
    $[25] = t2;
  } else {
    t2 = $[25];
  }
  return t2;
});
BoostedDropCardHomeContent.displayName = "BoostedDropCardHomeContent";
export default BoostedDropCardHomeContent;